import { useRouter } from 'next/router'
import { ElementType, FC } from 'react'
import { classNames } from '@/utils/methods'
import Link from 'next/link'
import useAuthorization from '@/hooks/useAuthorization'
interface NavigationItem {
  name: string
  href: string
  icon: ElementType
  target?: string
  current: boolean
}

export const NavigationLink: FC<
  React.PropsWithChildren<{ item: NavigationItem }>
> = ({ item }) => {
  const router = useRouter()
  const { pathname } = router
  const isCurrent = (() => item.href === pathname)()
  // Prefetch the route on hover/focus so links below the fold (which next/link
  // only prefetches once scrolled into view) are warm before the click.
  const prefetch = () => {
    // Ignore prefetch failures (e.g. offline); it's a best-effort optimization.
    const ignore = () => undefined
    if (!item.target) void router.prefetch(item.href).catch(ignore)
  }
  /*
   * We don't want to show the user a link that is restricted based on their user role.
   */
  const { isAllowed } = useAuthorization()
  if (!isAllowed(item.href)) {
    return null
  }
  return (
    <Link
      href={item.href}
      key={item.name}
      target={item.target}
      onMouseEnter={prefetch}
      onFocus={prefetch}
      className={classNames(
        isCurrent
          ? `bg-brand-800 text-white`
          : `text-brand-100 hover:text-white hover:bg-brand-600`,
        'group flex items-center px-2 py-2 lg:text-sm leading-6 font-medium rounded-md'
      )}
      aria-current={isCurrent ? 'page' : undefined}
      rel="noopener noreferrer"
    >
      <item.icon
        className={`mr-4 flex-shrink-0 h-6 w-6 text-brand-200`}
        aria-hidden="true"
      />

      {item.name}
    </Link>
  )
}
